import React, {Dispatch, ReactElement, SetStateAction, useEffect} from "react";
import {KubernetesAlert} from "../../pages/alerts/KubernetesAlert";
import axios from "../../utility/customAxios";
import {Pill} from "../Filter/Pill";
import {Popover, PopoverContent, PopoverTrigger} from "../ui/popover";
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList} from "../ui/command";
import {cn} from "../ui/lib/utils";
import {KnownService} from "../../types/service";
import {Alert} from "../../pages/alerts/model";

export function AlertFilterSelector(props: {
    alert: Alert,
    setAlert: Dispatch<SetStateAction<Alert>>
}) {
    let k8sAlert = (props.alert.alert as KubernetesAlert)

    const [filters, setFilters] = React.useState<Map<string, string[]>>(initialFilterMap(k8sAlert.filters.clusters, k8sAlert.filters.services))
    const [openClusterFilter, setOpenClusterFilter] = React.useState(false)
    const [openServiceFilter, setOpenServiceFilter] = React.useState(false)
    const [environments, setEnvironments] = React.useState<string[]>([])
    const [services, setServices] = React.useState<KnownService[]>([])
    const [selectedEnvironments, setSelectedEnvironments] = React.useState<string[]>([])

    useEffect(() => {
        // Fetch environments
        axios.get("/api/v1/environments").then((response) => {
            setEnvironments(response.data.environments)
        }).catch((e) => {
            console.error(e)
        })
    }, [])

    useEffect(() => {
        // Fetch environments
        axios.post("/api/v1/knownServices", {
            environments: selectedEnvironments
        }).then((response) => {
            setServices(response.data.services)
        }).catch((e) => {
            console.error(e)
        })
    }, [selectedEnvironments])


    useEffect(() => {
        let k8sAlert = (props.alert.alert as KubernetesAlert)
        k8sAlert.filters.clusters = filters.get("cluster") ?? []
        k8sAlert.filters.services = filters.get("service") ?? []
        props.setAlert({...props.alert, alert: k8sAlert})
    }, [filters])

    useEffect(() => {
        if (!filters.has("cluster")) {
            setSelectedEnvironments([])
        }
        filters.get("cluster")?.forEach((cluster) => {
            setSelectedEnvironments(filters.get("cluster") ?? [])
        })
    }, [filters]);

    let clusterPills: ReactElement[] = [];
    filters.get("cluster")?.forEach((cluster) => {
        clusterPills.push(<Pill key={cluster} attributeKey={"cluster"} attributeValue={cluster}
                                filter={filters}
                                setFilter={setFilters}
                                additionalRemoveAction={() => {
                                    setFilters((prev: Map<string, string[]>) => {
                                        const newFilter = new Map(prev);
                                        if (newFilter.has("service")) {
                                            newFilter.delete("service")
                                        }
                                        return newFilter
                                    })
                                }}
        />)
    })

    let servicePills: ReactElement[] = [];
    filters.get("service")?.forEach((service) => {
        servicePills.push(<Pill key={service} attributeKey={"service"} attributeValue={service}
                                filter={filters}
                                setFilter={setFilters}/>)
    })

    return <div className={"flex flex-col gap-4"}>
        <div className={"flex justify-start gap-2"}>
            <div
                className={"h-[36px] w-[36px] border border-primary bg-primarytransparent text-textlight flex flex-col justify-center text-center font-semibold rounded"}>
                3
            </div>
            <div className={"text-lg flex flex-col justify-center text-center text-textlight"}>
                Select Filters
            </div>
        </div>
        <div className={"text-textmedium"}>
            <div>
                The alert will fire for:
            </div>
            <div>
                <ul className={"list-disc list-inside"}>
                    <li>
                        {
                            k8sAlert.filters.clusters.length === 0 ? "All clusters" : "Only the following clusters: " + k8sAlert.filters.clusters.join(", ")
                        }
                    </li>
                    <li>
                        {
                            k8sAlert.filters.services.length === 0 ? "All services in each cluster" : "Only the following services: " + k8sAlert.filters.services.join(", ")
                        }
                    </li>
                </ul>
            </div>
        </div>
        <div className={"flex flex-col justify-start gap-4"}>
            <div className={"flex flex-wrap border bg-backgroundmedium rounded"}>
                <div className={"text-textmedium flex flex-col justify-center border-r bg-backgrounddark p-2"}>
                    Clusters
                </div>
                <Popover open={openClusterFilter}>
                    <PopoverTrigger asChild className={"flex grow"}>
                        <div onClick={() =>
                            setOpenClusterFilter(true)
                        } className={"flex flex-wrap gap-4 p-2 hover:cursor-pointer grow"}>
                            {
                                clusterPills.length === 0 &&
                                <div className={"text-textmedium"}>
                                    All Clusters
                                </div>
                            }
                            {clusterPills}
                        </div>
                    </PopoverTrigger>
                    <PopoverContent side={"bottom"} avoidCollisions={true}
                                    onFocusOutside={() => setOpenClusterFilter(false)}
                                    onEscapeKeyDown={() => setOpenClusterFilter(false)}
                                    onInteractOutside={() => setOpenClusterFilter(false)}
                                    className="p-0 text-textlight bg-backgroundmedium w-[50vw]"
                    >
                        <Command>
                            <CommandInput id={"free_text_input2"}
                                          className={cn("h-12 grow text-textlight ring-0 border-0 shadow-none focus-visible:border-0 focus-visible:ring-0 bg-backgroundmedium")}/>
                            <CommandList className={"text-textlight"}>
                                <CommandEmpty>No clusters found.</CommandEmpty>
                                <CommandGroup>
                                    {
                                        environments.filter(env => !filters.get("cluster")?.includes(env)).map((kv, index) => {
                                            return <CommandItem
                                                className={"w-full ariahover:cursor-pointer aria-selected:bg-secondarytransparenter aria-selected:border aria-selected:border-secondary aria-selected:text-textlight hover:bg-primarytransparent hover:text-textlight"}
                                                key={index} onSelect={() => {
                                                setOpenClusterFilter(false);

                                                setFilters((prev: Map<string, string[]>) => {
                                                    const newFilter = new Map(prev);
                                                    if (newFilter.has("cluster")) {
                                                        newFilter.get("cluster")?.push(kv);
                                                    } else {
                                                        newFilter.set("cluster", [kv]);
                                                    }
                                                    if (newFilter.has("service")) {
                                                        newFilter.delete("service")
                                                    }
                                                    return newFilter;
                                                })
                                            }}>
                                                {kv}
                                            </CommandItem>
                                        })
                                    }
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>
            </div>
            <div className={"flex flex-wrap border bg-backgroundmedium rounded"}>
                <div className={"text-textmedium border-r bg-backgrounddark p-2"}>
                    Services
                </div>
                <Popover open={openServiceFilter}>
                    <PopoverTrigger asChild className={"flex grow"}>
                        <div onClick={() =>
                            setOpenServiceFilter(true)
                        } className={"flex flex-wrap gap-4 p-2 hover:cursor-pointer grow"}>
                            {
                                servicePills.length === 0 &&
                                <div className={"text-textmedium"}>
                                    All Services
                                </div>
                            }
                            {servicePills}
                        </div>
                    </PopoverTrigger>
                    <PopoverContent side={"bottom"} avoidCollisions={true}
                                    onFocusOutside={() => setOpenServiceFilter(false)}
                                    onEscapeKeyDown={() => setOpenServiceFilter(false)}
                                    onInteractOutside={() => setOpenServiceFilter(false)}
                                    className="p-0 text-textlight bg-backgroundmedium w-[50vw]"
                    >
                        <Command>
                            <CommandInput id={"free_text_input2"}
                                          className={cn("h-12 grow text-textlight ring-0 border-0 shadow-none focus-visible:border-0 focus-visible:ring-0 bg-backgroundmedium")}/>
                            <CommandList className={"text-textlight"}>
                                <CommandEmpty>No services found.</CommandEmpty>
                                <CommandGroup>
                                    {
                                        services.filter(service => !filters.get("service")?.includes(service.displayName)).map((kv, index) => {
                                            return <CommandItem
                                                className={"w-full ariahover:cursor-pointer aria-selected:bg-secondarytransparenter aria-selected:border aria-selected:border-secondary aria-selected:text-textlight hover:bg-primarytransparent hover:text-textlight"}
                                                key={index} onSelect={() => {
                                                setOpenServiceFilter(false);

                                                setFilters((prev: Map<string, string[]>) => {
                                                    const newFilter = new Map(prev);
                                                    if (newFilter.has("service")) {
                                                        newFilter.get("service")?.push(kv.serviceName);
                                                    } else {
                                                        newFilter.set("service", [kv.serviceName]);
                                                    }
                                                    return newFilter;
                                                })
                                            }}>
                                                {kv.displayName}
                                            </CommandItem>
                                        })
                                    }
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>
            </div>
        </div>
    </div>
}

function initialFilterMap(clusters: string[], services: string[]): Map<string, string[]> {
    let filterMap = new Map<string, string[]>()
    filterMap.set("cluster", clusters)
    filterMap.set("service", services)
    return filterMap
}